import {html} from "lit";
import {customElement} from "lit/decorators/custom-element.js";
import {translate} from "lit-translate";
import {container} from "tsyringe";
import {appState} from "../../app.state";
import {AuthorizationService} from "../../services/authorization.service";
import {
    bindTo,
    ComplexFormControl,
    FormControlNotification
} from "@ha/formhandler";
import {AbstractLitElement} from "../abstract-lit.element";
import {filter, takeUntil} from "rxjs";
import {state} from "lit/decorators/state.js";
import {createLoginForm, isLoginFormSavable, type LoginFormData} from "./login-form";

@customElement(`ha-form-login`)
export class HaFormLoginComponent extends AbstractLitElement {

    protected _form: ComplexFormControl<LoginFormData, unknown> = createLoginForm();

    @state()
    protected _isSavable: boolean = false;

    connectedCallback() {
        super.connectedCallback();

        this._form.event$.pipe(
            takeUntil(this._disconnected$),
            filter((value: FormControlNotification) => value.event === `value.changed`)
        ).subscribe(() => this._isSavable = isLoginFormSavable(this._form))
    }

    protected render(): unknown {
        // Review 01 (2026-08-19), WARNING (Linse A): a plain <header>/<main>
        // split (matching every other formular__header/formular__main dialog
        // form) drops the <form> element entirely — fine for those forms, but
        // on the login screen it also weakens browser/password-manager
        // heuristics for detecting and offering to save credentials, since
        // those key heavily off a wrapping <form>. Kept here, wrapping the
        // whole header/main split, purely for that signal — no submit
        // behaviour changes (buttons stay type="button", Enter is still
        // handled via @pressed-enter, not native form submission).
        return html`
            <form>
                <header class="formular__header">
                    <div class="button-group">
                        <button type="button"
                                class="login buttonform-marker"
                                data-label="${translate('buttons.login')}"
                                ?disabled=${!this._isSavable}
                                @click=${this._onClickRequestAuthorization}
                        >
                            <div class="hover-effect"></div>
                        </button>
                    </div>
                </header>
                <main class="formular__main">
                    <ha-input-text class="max9999-flexrow " data-type="user-name" label="Nutzername"
                                   ${bindTo(this._form, `username`)}
                    ></ha-input-text>
                    <ha-input-text class="max9999-flexrow " data-type="password" label="Passwort"
                                   ${bindTo(this._form, `password`)}
                                   obscure
                                   disable-autocomplete
                                   @pressed-enter=${this._onClickRequestAuthorization}
                    ></ha-input-text>
                </main>
            </form>
        `;
    }

    private async _onClickRequestAuthorization(): Promise<void> {
        // Enter reaches this handler too (@pressed-enter), past the disabled button, so the
        // required fields are checked here as well.
        if (!(this._form && this._form.isDirty) || !isLoginFormSavable(this._form)) {
            return;
        }

        const value = this._form.getValue();
        if (value === null) {
            return;
        }

        const {username, password} = value;
        const success = await container.resolve(AuthorizationService).login(username, password);
        if (success) {
            const target = [null, `null`].includes(appState.redirectAfterLogin) ? `/` : appState.redirectAfterLogin!;
            appState.redirectAfterLogin = null;
            appState.router.goto(target).then();
        }
    }
}